import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  Link,
  Paper,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router-dom";
import {
  humanizeTransactionErrorMessage,
  isProjectTransactionCompleted,
  isProjectTransactionErrored,
  isProjectTransactionInProgress,
  isProjectTransactionTerminal,
  useClearTransactionNotification,
  useProjectDetails,
  useProjectTransactionNotifications
} from "@/api/api";
import useApplyCompletedGenerationTransactions from "@/hooks/useApplyCompletedGenerationTransactions";
import usePollInProgressGenerationTransactions from "@/hooks/usePollInProgressGenerationTransactions";
import type { ProjectTransactionNotification } from "@/types/api";
import {
  appendLibraryMediaHighlightToPath,
  projectPanelPathForTransactionTag
} from "@/types/routes";
import { libraryHighlightForCompletedTransaction } from "@/services/transactionNotificationNavigation";
import { transactionLabelForShotOrVideoGeneration } from "@/services/storyboard";
import type { ProjectDetailsProject, ProjectDetailsStoryboardShot } from "@/types/project";
import { toastError } from "@/utils/toast";

interface TransactionNotificationsMenuProps {
  readonly projectId: string | undefined;
  readonly disabled?: boolean;
}

const headerButtonSx = {
  height: 32,
  color: "text.secondary",
  textTransform: "none",
  letterSpacing: 0.15,
  fontSize: 13,
  px: 1.5,
  gap: 1,
  minWidth: 0
} as const;

const dropdownPaperSx = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  width: 360,
  maxHeight: 360,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "divider",
  zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal
} as const;

const clearAllButtonSx = {
  minWidth: 0,
  color: "text.primary",
  textTransform: "uppercase",
  letterSpacing: 0.46,
  fontSize: 13,
  fontWeight: 500,
  px: 0.5,
  py: 0.25
} as const;

const notificationLinkSx = {
  display: "block",
  color: "text.primary",
  fontWeight: 500,
  fontSize: "0.875rem",
  lineHeight: 1.43,
  textDecoration: "none",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    textDecoration: "underline"
  }
} as const;

function transactionDisplayLabel(
  transaction: ProjectTransactionNotification,
  storyboardShots: readonly ProjectDetailsStoryboardShot[] | undefined
): string {
  const shotOrVideoLabel = transactionLabelForShotOrVideoGeneration(transaction, storyboardShots);
  if (shotOrVideoLabel) {
    return shotOrVideoLabel;
  }

  const name = transaction.display_name?.trim();
  if (name) {
    return name;
  }

  switch (transaction.transaction_type) {
    case "I":
      return "Image generation";
    case "V":
      return "Video generation";
    case "VE":
      return "Video extension";
    default:
      return "Generation";
  }
}

function formatTransactionErrorOutput(output: string | null): string {
  if (!output?.trim()) {
    return "Generation failed.";
  }

  const trimmed = output.trim();
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === "string" && parsed.length > 0) {
      return humanizeTransactionErrorMessage(parsed);
    }

    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      const record = parsed as Record<string, unknown>;
      if (typeof record["error"] === "string" && record["error"].length > 0) {
        return humanizeTransactionErrorMessage(record["error"]);
      }

      if (typeof record["message"] === "string" && record["message"].length > 0) {
        return humanizeTransactionErrorMessage(record["message"]);
      }
    }
  } catch {
    // Plain-text error payload.
  }

  return humanizeTransactionErrorMessage(trimmed.replace(/^Error:\s*/i, ""));
}

function NotificationStatusIcon({
  completed,
  errored,
  inProgress
}: {
  readonly completed: boolean;
  readonly errored: boolean;
  readonly inProgress: boolean;
}) {
  if (errored) {
    return <ErrorOutlineOutlinedIcon fontSize="small" color="error" aria-label="Failed" />;
  }

  if (completed) {
    return <CheckCircleIcon fontSize="small" color="success" aria-label="Completed" />;
  }

  if (inProgress) {
    return (
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: (theme) => alpha(theme.palette.text.primary, 0.35)
        }}
        aria-hidden
      />
    );
  }

  return null;
}

function notificationNavigationPath(
  projectId: string,
  transaction: ProjectTransactionNotification,
  project: ProjectDetailsProject | null | undefined
): string | null {
  const basePath = projectPanelPathForTransactionTag(projectId, transaction.tag_type, transaction.tag_id);
  if (basePath === null) {
    return null;
  }

  const libraryHighlight = libraryHighlightForCompletedTransaction(transaction, project);
  if (libraryHighlight === null) {
    return basePath;
  }

  return appendLibraryMediaHighlightToPath(basePath, libraryHighlight);
}

function NotificationLabel({
  projectId,
  transaction,
  storyboardShots,
  project,
  onNavigate
}: {
  readonly projectId: string;
  readonly transaction: ProjectTransactionNotification;
  readonly storyboardShots: readonly ProjectDetailsStoryboardShot[] | undefined;
  readonly project: ProjectDetailsProject | null | undefined;
  readonly onNavigate: () => void;
}) {
  const label = transactionDisplayLabel(transaction, storyboardShots);
  const navigationPath = notificationNavigationPath(projectId, transaction, project);

  if (navigationPath === null) {
    return (
      <Typography variant="body2" noWrap sx={{ color: "text.primary", fontWeight: 500 }}>
        {label}
      </Typography>
    );
  }

  return (
    <Link
      component={RouterLink}
      to={navigationPath}
      underline="none"
      sx={notificationLinkSx}
      onClick={onNavigate}
    >
      {label}
    </Link>
  );
}

const TransactionNotificationsMenu = ({ projectId, disabled = false }: TransactionNotificationsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const notificationsQuery = useProjectTransactionNotifications(projectId, { enabled: !disabled });
  const projectDetailsQuery = useProjectDetails(disabled ? null : (projectId ?? null));
  const clearNotificationMutation = useClearTransactionNotification();

  const items = notificationsQuery.data ?? [];
  const storyboardShots = projectDetailsQuery.data?.project?.["storyboard-shots"];
  const project = projectDetailsQuery.data?.project ?? null;
  usePollInProgressGenerationTransactions(projectId, items, { enabled: !disabled });
  useApplyCompletedGenerationTransactions(projectId, items);

  const inProgressCount = useMemo(
    () => items.filter((item) => isProjectTransactionInProgress(item.transaction_status)).length,
    [items]
  );

  const dismissibleCount = useMemo(
    () => items.filter((item) => isProjectTransactionTerminal(item.transaction_status)).length,
    [items]
  );

  if (!projectId || items.length === 0) {
    return null;
  }

  const summaryLabel =
    inProgressCount > 0
      ? `Generating ${inProgressCount} item${inProgressCount === 1 ? "" : "s"}`
      : `${items.length} item${items.length === 1 ? "" : "s"}`;

  const handleDismiss = async (transactionId: string) => {
    try {
      await clearNotificationMutation.mutateAsync(transactionId);
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Failed to clear notification.");
    }
  };

  const handleClearAll = async () => {
    const transactionIdsBottomToTop = items
      .toReversed()
      .filter((item) => isProjectTransactionTerminal(item.transaction_status))
      .map((item) => item.transaction_id);

    if (transactionIdsBottomToTop.length === 0) {
      return;
    }

    setIsClearingAll(true);
    try {
      for (const transactionId of transactionIdsBottomToTop) {
        try {
          // oxlint-disable-next-line no-await-in-loop -- sequential on purpose: stop at the first failure instead of firing all clears at once
          await clearNotificationMutation.mutateAsync(transactionId);
        } catch (error) {
          toastError(error instanceof Error ? error.message : "Failed to clear notification.");
          break;
        }
      }
    } finally {
      setIsClearingAll(false);
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsOpen(false);
      }}
    >
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIcon={<ExpandMoreIcon sx={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
          startIcon={
            inProgressCount > 0 ? <CircularProgress size={14} thickness={5} color="primary" aria-hidden /> : undefined
          }
          sx={headerButtonSx}
          onClick={() => {
            setIsOpen((open) => !open);
          }}
          aria-haspopup="true"
          aria-expanded={isOpen ? "true" : "false"}
          aria-label="Open generation notifications"
        >
          {summaryLabel}
        </Button>
        {isOpen ? (
          <Paper elevation={0} sx={dropdownPaperSx}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, pt: 0.75, pb: 0.5, flexShrink: 0 }}>
              <Button
                variant="text"
                size="small"
                aria-label="Clear all notifications"
                disabled={dismissibleCount === 0 || isClearingAll || clearNotificationMutation.isPending}
                onClick={() => {
                  void handleClearAll();
                }}
                sx={clearAllButtonSx}
              >
                Clear all
              </Button>
            </Box>
            <Divider />
            <Box sx={{ overflowY: "auto", py: 0.5, maxHeight: 320 }}>
              {items.map((item, index) => {
                const errored = isProjectTransactionErrored(item.transaction_status);
                const completed = isProjectTransactionCompleted(item.transaction_status);
                const inProgress = isProjectTransactionInProgress(item.transaction_status);
                const showDismiss = isProjectTransactionTerminal(item.transaction_status);
                const isClearing =
                  clearNotificationMutation.isPending &&
                  clearNotificationMutation.variables === item.transaction_id;

                return (
                  <Box key={item.transaction_id}>
                    {index > 0 ? <Divider /> : null}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        px: 1.5,
                        py: 1.25
                      }}
                    >
                      <Box sx={{ pt: 0.25, width: 20, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                        <NotificationStatusIcon completed={completed} errored={errored} inProgress={inProgress} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <NotificationLabel
                          projectId={projectId}
                          transaction={item}
                          storyboardShots={storyboardShots}
                          project={project}
                          onNavigate={closeMenu}
                        />
                        {errored ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              color: "warning.main",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word"
                            }}
                          >
                            {formatTransactionErrorOutput(item.output)}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box sx={{ width: 28, flexShrink: 0, display: "flex", justifyContent: "center", pt: 0.25 }}>
                        {inProgress ? (
                          <CircularProgress size={16} thickness={5} aria-label="In progress" />
                        ) : showDismiss ? (
                          <IconButton
                            size="small"
                            aria-label={`Dismiss ${transactionDisplayLabel(item, storyboardShots)}`}
                            disabled={isClearing || isClearingAll}
                            onClick={() => {
                              void handleDismiss(item.transaction_id);
                            }}
                            sx={{ color: "text.secondary" }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
};

export default TransactionNotificationsMenu;
